import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styles from './alert-banner.module.scss';
import { UIIcon, UIText } from '@/ui';
import { UnassignedAlertsModal } from './unassigned-alerts-modal';
import { useUnassignedAlerts } from '../providers';
import { useNotificationSettings } from '@/common/hooks';
import alarmWav from '../assets/sounds/alarm.wav';
import useSound from 'use-sound';
import { useTasksTranslation } from '../hooks';

const REPEAT_INTERVAL_MS = 30_000;

export function AlertBanner(): ReactElement {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { t } = useTasksTranslation();
    const { tasks } = useUnassignedAlerts();

    const bannerMessage = useMemo(
        () => t('components.alert_banner.unassigned', { count: tasks.length }),
        [tasks.length, t]
    );

    // We want to track new tasks, and open the modal when a new task alert is added
    const { showAlertPopup, playAlertSound } = useNotificationSettings();
    const [play] = useSound(alarmWav);

    const alertIds = useMemo(
        () => tasks.map((task) => task.alert?.id),
        [tasks]
    );
    const alertIdsSeen = useRef(new Set<string | undefined>());
    useEffect(() => {
        let hasUnseenAlerts = false;
        alertIds.forEach((alertId) => {
            if (!alertIdsSeen.current.has(alertId)) {
                alertIdsSeen.current.add(alertId);
                hasUnseenAlerts = true;
            }
        });
        if (hasUnseenAlerts) {
            if (showAlertPopup) {
                setIsModalOpen(true);
            }
            if (playAlertSound) {
                play();
            }
        }
    }, [alertIds, showAlertPopup, playAlertSound, play]);

    const unacknowledgedCount = tasks.length;
    useEffect(() => {
        if (!playAlertSound || unacknowledgedCount === 0) {
            return;
        }
        const intervalId = setInterval(() => play(), REPEAT_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [playAlertSound, unacknowledgedCount, play]);

    if (tasks.length === 0) return <></>;

    return (
        <>
            <button
                className={styles.container}
                onClick={() => {
                    setIsModalOpen(true);
                }}
            >
                <UIIcon name="alert-triangle" size={18} color="white" />
                <UIText variant="semiLarge" type="white" bold>
                    {bannerMessage}
                </UIText>
            </button>
            <UnassignedAlertsModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                }}
            />
        </>
    );
}
